import React, { useEffect, useState } from "react";
import { Box, IconButton, Typography, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

export default function EventPopup({ event }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Slight delay so page loads first, then popup appears smoothly
    const timer = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(timer);
  }, []);

  if (!open || !event) return null;

  const message = `I want to register for the event: ${event.titleEn || event.title}`;
  const whatsappLink = `https://wa.me/${event.whatsappNumber || "918903791212"}?text=${encodeURIComponent(message)}`;

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={() => setOpen(false)}
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.7)",
          zIndex: 9000,
          animation: "backdropFadeIn 0.4s ease",
          "@keyframes backdropFadeIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      />

      {/* Popup Container */}
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9001,
          maxWidth: { xs: "92vw", sm: "480px", md: "520px" },
          width: "100%",
          animation: "popupSlideIn 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)",
          "@keyframes popupSlideIn": {
            from: { opacity: 0, transform: "translate(-50%, -55%) scale(0.88)" },
            to: { opacity: 1, transform: "translate(-50%, -50%) scale(1)" },
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={() => setOpen(false)}
          aria-label="Close popup"
          sx={{
            position: "absolute",
            top: -14,
            right: -14,
            backgroundColor: "#BF0603",
            color: "#fff",
            width: 36,
            height: 36,
            zIndex: 10,
            boxShadow: "0 4px 12px rgba(191,6,3,0.5)",
            "&:hover": {
              backgroundColor: "#9e0402",
              transform: "rotate(90deg)",
              transition: "transform 0.25s ease",
            },
            transition: "background-color 0.2s ease",
          }}
        >
          <CloseIcon sx={{ fontSize: "1.1rem" }} />
        </IconButton>

        {/* Card */}
        <Box
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 30px 80px rgba(0,0,0,0.45), 0 0 0 3px rgba(212,175,55,0.6)",
            backgroundColor: "#fff",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          {/* Banner Image */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#FFF8E1",
            }}
          >
            <img
              src={event.bannerImage || event.image}
              alt={event.titleEn || event.title}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "65vh",
                display: "block",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Bottom Action Bar */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: "linear-gradient(135deg, #BF0603 0%, #8B0000 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontFamily: "Quicksand",
                  fontWeight: 800,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  lineHeight: 1.2,
                }}
              >
                {event.titleEn || event.title}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  fontFamily: "Varela Round",
                  fontSize: "0.82rem",
                  mt: 0.3,
                }}
              >
                📅 {event.date} &nbsp;|&nbsp; 💰 {event.fee}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<WhatsAppIcon />}
              onClick={() => window.open(whatsappLink, "_blank")}
              sx={{
                backgroundColor: "#25d366",
                color: "#fff",
                fontWeight: 700,
                fontFamily: "Quicksand",
                px: 2.5,
                py: 1,
                borderRadius: "20px",
                fontSize: "0.85rem",
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(37,211,102,0.4)",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#1ebe5d" },
              }}
            >
              Register Now
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}
